/*
 * Header accessibility behaviour.
 *  1. Sticky header: publish the measured header height as --hfs-header-h so that
 *     html { scroll-padding-top } (assets/hfs-header.css) keeps focused elements clear of it.
 *  2. Account menu (desktop): disclosure button that opens on click, Enter and Space, and on
 *     hover for mouse users; closes on Escape (focus returns to the button), when focus leaves
 *     the menu, and on an outside click.
 *  3. Fallbacks for a script that never ran (see initFallbacks).
 */
(function () {
  'use strict';

  /* 1. Header height -> CSS variable */
  function initHeaderHeight() {
    var header = document.querySelector('.fixed-announce');
    if (!header) return;

    function setHeight() {
      var h = Math.ceil(header.getBoundingClientRect().height) + 'px';
      document.documentElement.style.setProperty('--hfs-header-h', h);
      /* HFS (FBL-013): --hfs-header-static-h is the height of the header in its normal (non-sticky) state, used for the
         page's top padding on phones (custom-style.css). While .is-sticky is set (position:fixed, and the announcement
         hidden at >=1280px) the live height is not the height the page has to clear, so the last non-sticky value is kept. */
      if (!header.classList.contains('is-sticky')) document.documentElement.style.setProperty('--hfs-header-static-h', h);
    }

    setHeight();
    window.addEventListener('resize', setHeight);
    window.addEventListener('load', setHeight);
    if ('ResizeObserver' in window) new ResizeObserver(setHeight).observe(header);
    /* HFS (FBL-013): re-measure when .is-sticky is removed, as the size may have changed while it was set. */
    if ('MutationObserver' in window) new MutationObserver(setHeight).observe(header, { attributes: true, attributeFilter: ['class'] });
  }

  /* 2. Account menu */
  function initAccountMenu() {
    var root = document.querySelector('.ctm_users');
    if (!root) return;
    var button = root.querySelector('button.ctm_users__main');
    var menu = root.querySelector('.ctm_users__drop');
    if (!button || !menu) return;

    var hoverOpened = false;

    function isOpen() {
      return button.getAttribute('aria-expanded') === 'true';
    }

    function open() {
      menu.removeAttribute('hidden');
      button.setAttribute('aria-expanded', 'true');
    }

    function close() {
      menu.setAttribute('hidden', '');
      button.setAttribute('aria-expanded', 'false');
      hoverOpened = false;
    }

    // A native <button> already fires click for Enter and Space.
    button.addEventListener('click', function () {
      if (isOpen() && hoverOpened) {
        // The pointer opened it on hover; a click keeps it open instead of toggling it shut.
        hoverOpened = false;
        return;
      }
      isOpen() ? close() : open();
    });

    // Keep hover opening for mouse users. Escape still dismisses it (WCAG 1.4.13).
    root.addEventListener('mouseenter', function () {
      if (isOpen()) return;
      open();
      hoverOpened = true;
    });

    root.addEventListener('mouseleave', function () {
      if (hoverOpened && !root.contains(document.activeElement)) close();
      hoverOpened = false;
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape' || !isOpen()) return;
      var focusInside = root.contains(document.activeElement);
      close();
      if (focusInside) button.focus();
    });

    root.addEventListener('focusout', function () {
      setTimeout(function () {
        if (isOpen() && !root.contains(document.activeElement) && !hoverOpened) close();
      });
    });

    document.addEventListener('click', function (event) {
      if (isOpen() && !root.contains(event.target)) close();
    });
  }

  /* 3. HFS (layout shift, growth priorities 2026-10-01): some markup waits for a deferred script to finish it. Deferred
     scripts run before the load event, so if one was blocked or failed, these undo the waiting a few seconds after it:
     - below 1285px hfs-carousel.css shows only the announcements Slick will show, until it starts; if it has not
       started, hfs-no-slick lifts those rules and every message is on show again, as before them;
     - whatever is still marked data-hfs-pending (the sizing guides' Contents nav and calculator) is hidden, instead of
       holding an empty space or showing controls that do nothing. */
  function initFallbacks() {
    window.addEventListener('load', function () {
      setTimeout(function () {
        var slider = document.querySelector('.announce_slider');
        var sliding = !window.matchMedia || window.matchMedia('(max-width: 1284.98px)').matches; /* Slick is off above */
        if (slider && sliding && !slider.classList.contains('slick-initialized')) document.documentElement.classList.add('hfs-no-slick');
        Array.prototype.forEach.call(document.querySelectorAll('[data-hfs-pending]'), function (el) { el.hidden = true; });
      }, 3000);
    });
  }

  initHeaderHeight();
  initAccountMenu();
  initFallbacks();
})();
